import { Box, Paper, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import RefreshButton from "../../components/button/RefreshButton";
import { MainContainer } from "../../components/container/MainContainer";
import { PageTitleContainer } from "../../components/container/PageTitleContainer";
import ResizableBox from "../../components/container/ResizableBox";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import ContextMenu from "../../components/context-menu/ContextMenu";
import ExportAsXLSXButton from "../../components/export/ExportAsXLSXButton";
import AgGrid from "../../components/grid/AgGrid";
import { defaultColDef, floatFormater, leftAlignColDef, rightAlignColDef } from "../../components/grid/columnDefinitions/columnDefinitions";
import SearchBox from "../../components/input/SearchBox";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { Building } from "../../db_access/building";
import { Process } from "../../db_access/process";
import CarbonImpactSummary from "../../environment-page-components/CarbonImpactSummary";
import Footprint from "../../environment-page-components/Footprint";
import SystemBoundary from "../../environment-page-components/SystemBoundary";
import SankeyDiagram from "../../environment-page-components/SankeyDiagram";

const BuildingArchive = () => {
  const { t } = useTranslation();
  const { refresh, setRefresh, selectedLanguage } = useContextProvider();
  const gridRef = useRef(null);
  const [buildings, setBuildings] = useState([]);
  const [selectedBuildings, setSelectedBuildings] = useState([]);
  const [processData, setProcessData] = useState([]);
  const [resultsData, setResultsData] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    Building.get_mine_archive()
      .then((res) => res.json())
      .then((data) => setBuildings(data));
  }, [refresh]);

  useEffect(() => {
    if (selectedBuildings.length === 0) {
      setProcessData([]);
      setResultsData(null);
    } else {
      const buildingId = selectedBuildings[0].id;

      // Fetch Process Data
      Process.get_by_building_ids([buildingId])
        .then((res) => res.json())
        .then((data) => setProcessData(data));

      // Fetch Results Data for Sankey Breakdown
      Building.get_results({ building_id: buildingId, filter_category: "Global" })
        .then((res) => res.json())
        .then((data) => setResultsData(data));
    }
  }, [selectedBuildings]);

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setGridOption("quickFilterText", searchText);
    }
  }, [searchText]);

  const unarchiveBuilding = (selectedBuildings) => {
    Building.unarchive(selectedBuildings.map((s) => s.id)).finally(() => {
      setSelectedBuildings([]);
      setRefresh((r) => !r);
    });
  };

  const deleteBuilding = (selectedBuildings) => {
    Building.delete(selectedBuildings.map((s) => s.id)).finally(() => {
      setSelectedBuildings([]);
      setRefresh((r) => !r);
    });
  };

  const onSelectionChanged = (event) => {
    const selected = event.api.getSelectedRows();
    setSelectedBuildings(selected);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    setMenuPosition({ x: e.pageX, y: e.pageY });
    setMenuVisible(true);
  };

  const handleCloseMenu = () => {
    setMenuVisible(false);
  };

  const colDefs = [
    { field: "id", hide: true },
    {
      headerName: "Name",
      field: "name",
      headerCheckboxSelection: true,
      cellStyle: { fontWeight: 500, ...leftAlignColDef.cellStyle },
      ...defaultColDef,
    },
    {
      headerName: "Location",
      field: "location",
      ...defaultColDef,
      ...leftAlignColDef,
    },
    {
      headerName: "Building Category",
      field: "building_category",
      ...defaultColDef,
      ...leftAlignColDef,
    },
    {
      headerName: "Year",
      field: "year",
      ...defaultColDef,
      ...leftAlignColDef,
    },
    {
      headerName: "GWP (kg CO₂e)",
      field: "gwp",
      ...rightAlignColDef,
      ...defaultColDef,
      ...floatFormater,
    },
  ];

  const contextMenuOptions = [
    { label: "Unarchive", action: unarchiveBuilding, needSelectedItems: true },
    { label: "Delete", action: deleteBuilding, needSelectedItems: true },
  ];

  return (
    <MainContainer>
      <PageTitleContainer>
        <Box>
          <Typography variant="Page Title">{t("Building Archive")}</Typography>
          <Typography variant="Page Subtitle">{t("View and manage your archived buildings")}</Typography>
        </Box>
      </PageTitleContainer>

      <Paper variant="Table Container">
        <TableHeaderContainer>
          <TableTitleContainer>
            <Typography variant="Table Title">
              {t("Archived Buildings")} ({buildings.length})
            </Typography>
          </TableTitleContainer>
          <TableHeaderToolsContainer>
            <SearchBox text={searchText} setText={setSearchText} />
            <ExportAsXLSXButton data={buildings} label="Export as .xlsx" filename="archived_buildings.xlsx" />
            <RefreshButton />
          </TableHeaderToolsContainer>
        </TableHeaderContainer>
        <Box>
          <ResizableBox>
            <AgGrid ref={gridRef} columnDefs={colDefs} rowData={buildings} onSelectionChanged={onSelectionChanged} handleContextMenu={handleContextMenu} headerCheckbox={false} />
          </ResizableBox>
        </Box>
      </Paper>

      <Box sx={{ paddingY: 1 }} />

      <Box sx={{ display: selectedBuildings.length === 1 ? "block" : "none", width: "100%", overflowX: "hidden" }}>
        <SystemBoundary processData={processData} selectedLanguage={selectedLanguage} isBuilding={true} />
        <CarbonImpactSummary selectedRow={selectedBuildings[0]} selectedLanguage={selectedLanguage} />
        <Footprint processData={processData} selectedLanguage={selectedLanguage} pageType="utilizer" />
        {processData.length > 0 && <SankeyDiagram processData={processData} />}
      </Box>

      <ContextMenu x={menuPosition.x} y={menuPosition.y} visible={menuVisible} onClose={handleCloseMenu} selectedItems={selectedBuildings} options={contextMenuOptions} />
    </MainContainer>
  );
};

export default BuildingArchive;
